<script>
    import onboardingService from '@/services/onboarding.service';
    import modalService from '@/services/core/modal.service';
    import Spinner from '@/components/shared/Spinner';
    import CompanySearchSelect from '@/components/shared/CompanySearchSelect';
    import CreateOnboardingSessionModal from '@/components/modals/onboarding/CreateOnboardingSessionModal';

    const GEAR_UP_STATUSES = ['Pending', 'Completed', 'Failed'];

    export default {
        components: { Spinner, CompanySearchSelect },
        data() {
            return {
                tab: 'gearUp',
                gearUpRows: [],
                gearUpStatus: 'All',
                gearUpLoaded: false,
                gearUpBusy: false,
                gearUpSyncing: false,
                gearUpNotice: null,
                gearUpError: null,
                rows: [],
                sessionsLoaded: false,
                selectedFilterCompany: null,
                openOnly: true,
                isLoading: false,
                loadError: null,
                recapBusy: false,
                recapError: null,
                recapText: ''
            };
        },
        computed: {
            // Counted here rather than asked for: the rows are already in memory, and a second
            // round trip for three numbers that must agree with the list is a way to show numbers
            // that disagree with the list.
            gearUpCounts() {
                const counts = { All: this.gearUpRows.length };
                GEAR_UP_STATUSES.forEach(s => { counts[s] = 0; });
                this.gearUpRows.forEach(row => {
                    if (counts[row.status] !== undefined) counts[row.status] += 1;
                });
                return counts;
            },
            gearUpFilters() {
                // Failed only earns a chip once something has failed; an always-zero filter is noise.
                return ['All', 'Pending', 'Completed']
                    .concat(this.gearUpCounts.Failed > 0 ? ['Failed'] : [])
                    .map(status => ({ status, count: this.gearUpCounts[status] || 0 }));
            },
            visibleGearUpRows() {
                return this.gearUpStatus === 'All'
                    ? this.gearUpRows
                    : this.gearUpRows.filter(row => row.status === this.gearUpStatus);
            }
        },
        methods: {
            apiError(err, fallback) {
                return err?.data?.message || err?.message || fallback;
            },
            companyFilterId() {
                const id = parseInt(this.selectedFilterCompany?.id, 10);
                return Number.isFinite(id) && id > 0 ? id : undefined;
            },
            statusClass(status) {
                if (status === 'Completed') return 'text-bg-success';
                if (status === 'Failed') return 'text-bg-danger';
                return 'text-bg-warning';
            },
            submittedLabel(value) {
                if (!value) return 'Unknown';
                const at = new Date(value);
                if (Number.isNaN(at.getTime())) return 'Unknown';

                const today = new Date();
                const sameDay = at.toDateString() === today.toDateString();
                const time = at.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
                return sameDay ? `Today ${time}` : at.toLocaleDateString();
            },
            setTab(tab) {
                this.tab = tab;
                // Each tab loads once. Switching back to something already read should be instant,
                // not another spinner over data that has not changed.
                if (tab === 'gearUp' && !this.gearUpLoaded) this.loadGearUp();
                else if (tab === 'sessions' && !this.sessionsLoaded) this.load();
                else if (tab === 'recap' && !this.recapText) this.loadRecap();
            },
            async syncGearUp() {
                this.gearUpSyncing = true;
                this.gearUpError = null;
                this.gearUpNotice = null;
                try {
                    const result = await onboardingService.syncGearUp();
                    const opened = result?.opened || 0;
                    // Zero is the ordinary answer, not a failure: most syncs find nothing new.
                    this.gearUpNotice = opened
                        ? `${opened} new submission${opened === 1 ? '' : 's'} read.`
                        : 'Nothing new since the last check.';
                    await this.loadGearUp();
                } catch (err) {
                    this.gearUpError = this.apiError(err, 'Could not reach Typeform.');
                } finally {
                    this.gearUpSyncing = false;
                }
            },
            async loadGearUp() {
                this.gearUpBusy = true;
                this.gearUpError = null;
                try {
                    this.gearUpRows = (await onboardingService.listGearUp()) || [];
                    this.gearUpLoaded = true;
                } catch (err) {
                    this.gearUpError = this.apiError(err, 'Could not load Gear Up submissions.');
                } finally {
                    this.gearUpBusy = false;
                }
            },
            async load() {
                this.isLoading = true;
                this.loadError = null;
                try {
                    this.rows = (await onboardingService.list(this.companyFilterId(), this.openOnly)) || [];
                    this.sessionsLoaded = true;
                } catch (err) {
                    this.loadError = this.apiError(err, 'Could not load onboarding sessions.');
                } finally {
                    this.isLoading = false;
                }
            },
            async loadRecap() {
                this.recapBusy = true;
                this.recapError = null;
                try {
                    const recap = await onboardingService.generateRecap();
                    this.recapText = recap?.text || '';
                } catch (err) {
                    this.recapError = this.apiError(err, 'Could not load automatic recap.');
                    this.recapText = '';
                } finally {
                    this.recapBusy = false;
                }
            },
            open(id) {
                if (!id) return;
                this.$router.push(`/onboarding/${id}`);
            },
            openGearUpRow(row) {
                if (!row?.submissionId) return;
                if (row.sessionId) this.open(row.sessionId);
                else this.$router.push(`/onboarding/gear-up/${row.submissionId}`);
            },
            openCreateModal() {
                modalService.show(CreateOnboardingSessionModal, {
                    onCreated: row => {
                        if (row?.id) this.$router.push(`/onboarding/${row.id}`);
                        else this.load();
                    }
                });
            },
            async closeSession(row, event) {
                event?.stopPropagation?.();
                const company = row.companyName || `company ${row.companyId}`;
                if (!window.confirm(
                    `Close the onboarding for ${company}? Its files stay, but no more imports run from it.`
                )) return;
                this.isLoading = true;
                try {
                    await onboardingService.close(row.id);
                    await this.load();
                } catch (err) {
                    this.loadError = this.apiError(err, 'Could not close session.');
                    this.isLoading = false;
                }
            }
        },
        mounted() {
            this.loadGearUp();
        }
    };
</script>

<template>
    <div class="container-fluid py-3">
        <div class="d-flex flex-wrap align-items-start justify-content-between gap-2 mb-3">
            <div>
                <h1 class="h3 mb-1">Gear Up</h1>
                <p class="text-muted mb-0">
                    Gear Up submissions land here from Typeform. Review what arrived; open a session
                    when you are ready — the company often already exists. Sessions hold the build plan
                    proposed from what was dropped in their Slack thread.
                </p>
            </div>
            <button
                v-if="tab === 'sessions'"
                class="btn btn-primary"
                type="button"
                @click="openCreateModal"
            >
                New session
            </button>
        </div>

        <ul class="nav nav-tabs mb-3">
            <li class="nav-item">
                <button class="nav-link" :class="{ active: tab === 'gearUp' }" type="button" @click="setTab('gearUp')">
                    Gear Up
                </button>
            </li>
            <li class="nav-item">
                <button class="nav-link" :class="{ active: tab === 'sessions' }" type="button" @click="setTab('sessions')">
                    Sessions
                </button>
            </li>
            <!-- Recap stays in code but hidden until its review flow ships.
            <li class="nav-item">
                <button class="nav-link" :class="{ active: tab === 'recap' }" type="button" @click="setTab('recap')">
                    Recap
                </button>
            </li>
            -->
        </ul>

        <template v-if="tab === 'gearUp'">
            <div v-if="gearUpError" class="alert alert-danger">{{ gearUpError }}</div>
            <div v-else-if="gearUpNotice" class="alert alert-info py-2">{{ gearUpNotice }}</div>

            <div class="d-flex flex-wrap align-items-center gap-2 mb-3">
                <div class="btn-group" role="group" aria-label="Filter submissions by status">
                    <button
                        v-for="filter in gearUpFilters"
                        :key="filter.status"
                        class="btn btn-sm"
                        :class="gearUpStatus === filter.status ? 'btn-secondary' : 'btn-outline-secondary'"
                        :aria-pressed="gearUpStatus === filter.status"
                        type="button"
                        @click="gearUpStatus = filter.status"
                    >
                        {{ filter.status }}
                        <span class="ms-1 opacity-75">{{ filter.count }}</span>
                    </button>
                </div>
                <button
                    class="btn btn-sm btn-outline-primary ms-auto"
                    type="button"
                    :disabled="gearUpSyncing || gearUpBusy"
                    @click="syncGearUp"
                >
                    {{ gearUpSyncing ? 'Checking Typeform…' : 'Check Typeform now' }}
                </button>
            </div>

            <Spinner v-if="gearUpBusy" />

            <div v-else-if="!gearUpRows.length" class="border rounded p-4 text-center">
                <p class="mb-1">No submissions read yet</p>
                <p class="text-muted mb-0">
                    The Gear Up form is checked every 15 minutes. New responses show up here on their own.
                </p>
            </div>

            <div v-else-if="!visibleGearUpRows.length" class="border rounded p-4 text-center text-muted">
                Nothing {{ gearUpStatus.toLowerCase() }} right now.
            </div>

            <table v-else class="table table-sm table-hover align-middle">
                <thead>
                    <tr>
                        <th scope="col">Company</th>
                        <th scope="col">Submitted</th>
                        <th scope="col">Status</th>
                        <th scope="col">From the form</th>
                        <th scope="col"><span class="visually-hidden">Actions</span></th>
                    </tr>
                </thead>
                <tbody>
                    <tr
                        v-for="row in visibleGearUpRows"
                        :key="row.submissionId"
                        class="cursor-pointer"
                        @click="openGearUpRow(row)"
                    >
                        <td>
                            <div>{{ row.companyName }}</div>
                            <div v-if="row.contactName" class="small text-muted">{{ row.contactName }}</div>
                        </td>
                        <td class="text-muted">{{ submittedLabel(row.submittedAt) }}</td>
                        <td>
                            <span class="badge" :class="statusClass(row.status)">{{ row.status }}</span>
                            <div class="small text-muted">
                                {{ row.sessionId ? 'Session linked' : 'Recorded — not linked yet' }}
                            </div>
                        </td>
                        <td class="text-muted">
                            <div>{{ row.transportCount }} transport · {{ row.pumperCount }} pumpers</div>
                            <div v-if="row.hasLeaseFile" class="small text-muted">
                                Lease file on Typeform
                            </div>
                        </td>
                        <td class="text-end">
                            <span class="link-primary">Review</span>
                        </td>
                    </tr>
                </tbody>
            </table>
        </template>

        <template v-else-if="tab === 'sessions'">
            <div class="row g-3 mb-4">
                <div class="col-md-4">
                    <label class="form-label" for="companyFilter">Company</label>
                    <CompanySearchSelect
                        v-model="selectedFilterCompany"
                        input-id="companyFilter"
                        placeholder="Northline Energy"
                    />
                </div>
                <div class="col-md-3 d-flex align-items-end">
                    <div class="form-check">
                        <input id="openOnly" v-model="openOnly" class="form-check-input" type="checkbox" @change="load" />
                        <label class="form-check-label" for="openOnly">Open only</label>
                    </div>
                </div>
                <div class="col-md-2 d-flex align-items-end">
                    <button class="btn btn-outline-primary" type="button" :disabled="isLoading" @click="load">
                        Filter
                    </button>
                </div>
            </div>

            <div v-if="loadError" class="alert alert-danger">{{ loadError }}</div>
            <Spinner v-if="isLoading" />

            <table v-else class="table table-sm table-hover align-middle">
                <thead>
                    <tr>
                        <th scope="col">Session</th>
                        <th scope="col">Company</th>
                        <th scope="col">Facility</th>
                        <th scope="col">Stage</th>
                        <th scope="col">Files</th>
                        <th scope="col">Updated</th>
                        <th scope="col"><span class="visually-hidden">Actions</span></th>
                    </tr>
                </thead>
                <tbody>
                    <tr v-for="row in rows" :key="row.id" class="cursor-pointer" @click="open(row.id)">
                        <td>{{ row.id }}</td>
                        <td>{{ row.companyName || row.companyId }}</td>
                        <td>{{ row.facilityDefaultName || 'All / multi' }}</td>
                        <td>
                            {{ row.stageName }}
                            <span v-if="!row.isOpen" class="badge text-bg-secondary ms-1">Closed</span>
                        </td>
                        <td>{{ (row.files && row.files.length) || 0 }}/{{ row.maxFiles || 10 }}</td>
                        <td>{{ row.updatedDate }}</td>
                        <td class="text-end" @click.stop>
                            <button
                                v-if="row.isOpen"
                                class="btn btn-outline-secondary btn-sm"
                                type="button"
                                @click="closeSession(row, $event)"
                            >
                                Close
                            </button>
                        </td>
                    </tr>
                    <tr v-if="!rows.length">
                        <td colspan="7" class="text-muted">No sessions yet.</td>
                    </tr>
                </tbody>
            </table>
        </template>

        <template v-else>
            <div v-if="recapError" class="alert alert-danger">{{ recapError }}</div>
            <Spinner v-if="recapBusy" />
            <textarea v-else class="form-control recap-readonly" readonly rows="18" :value="recapText" />
        </template>
    </div>
</template>

<style lang="scss" scoped>
.cursor-pointer {
    cursor: pointer;
}

.nav-link {
    cursor: pointer;
}

.recap-readonly {
    font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, 'Liberation Mono', 'Courier New', monospace;
    font-size: 0.9rem;
    line-height: 1.45;
    background-color: #f8f9fa;
    resize: vertical;
}
</style>
